/**
 * Registre AUTHORITATIVE des champs de comparaison — chunk 9.AB.1.
 *
 * ## Pourquoi ce fichier, et pourquoi ici
 *
 * Décision PO : « La classification garantie / métadonnée / technique doit venir du
 * serveur ou d'un registre shared authoritative, pas d'une liste divergente cachée
 * dans le composant React. » Et pour le rattachement d'une preuve négative :
 * « Documente les règles de rattachement `field_key` → famille de garantie.
 * N'utilise pas une proximité de nom improvisée dans l'UI. »
 *
 * Tout ce qui suit est donc **dérivé des contrats déjà déclarés** dans ce même
 * workspace. Aucune liste maintenue à la main, aucune heuristique de nom :
 *
 *   - `EXTRACTION_PASSES` déclare, passe par passe, la liste exacte des champs ;
 *   - `IDENTIFICATION_RESPONSE_SCHEMA` déclare les 10 champs de métadonnée du
 *     document que la passe `identification` produit.
 *
 * Une 8ᵉ passe ajoutée demain, ou un champ ajouté à un contrat existant, entre donc
 * dans le registre **sans toucher ce fichier**. C'est la propriété qui compte : une
 * liste recopiée aurait divergé au premier ajout, et la divergence se serait
 * traduite par une ligne mal classée sous les yeux d'un conseiller.
 *
 * ## Les familles de garantie ne sont pas une proximité de nom
 *
 * Une « famille » est un **groupe de préfixes déclaré dans un contrat de passe** :
 *
 *   passe `vie_dma`      → familles `vie_base` (4 champs) et `dma` (2)
 *   passe `amc`          → famille  `amc` (7)
 *   passe `dentaire`     → famille  `dentaire` (6)
 *   passe `invalidite`   → familles `ic_court_terme` (4) et `ic_long_terme` (4)
 *   passes `primes`, `conditions`, `identification` → **aucune famille**
 *
 * Les trois dernières n'ont aucun préfixe : leurs champs sont plats
 * (`prime_mensuelle_total`, `exclusions`, `admissibility_hours`…). Ils reçoivent
 * donc `family = null`, et c'est une propriété de **sûreté**, pas une lacune : sans
 * famille, aucune preuve négative ne peut être rattachée par ce mécanisme, donc
 * aucune inférence hasardeuse n'est possible là où le contrat ne la fonde pas.
 *
 * Conséquence concrète, mesurée sur le smoke 9.AA.1b : Verchères porte
 * `exclusions = "Invalidité de longue durée non offerte ; …"`. Cette mention est
 * vraie, mais `exclusions` appartient à la passe `conditions`, sans famille. Elle
 * **ne qualifie donc pas** `ic_long_terme.percentage` comme non offert. Seul
 * `ic_long_terme.definition = "NON OFFERTE"`, de la même famille, le fait.
 * C'est exactement la propagation que la décision PO interdit.
 */

import { EXTRACTION_PASSES } from "../prompts/extraction.js";
import { IDENTIFICATION_RESPONSE_SCHEMA } from "../prompts/identification.js";

/**
 * Nature d'un champ vis-à-vis de la comparaison entre assureurs.
 *
 * `unknown` n'est pas un fourre-tout : c'est la classe des champs qu'aucun contrat
 * ne déclare. Ils restent **visibles par défaut**, parce que masquer ce qu'on ne
 * reconnaît pas serait masquer de la donnée réelle — un champ produit par un modèle
 * mis à jour, par exemple.
 */
export type FieldComparability =
  /** Champ de garantie : c'est ce qu'un conseiller compare. */
  | "guarantee"
  /** Métadonnée du document (assureur détecté, langue, sections…). */
  | "document_metadata"
  /** Débris techniques d'extraction — clés `extra_*` non reconnues. */
  | "technical"
  /** Non déclaré par aucun contrat : visible par défaut, jamais masqué en silence. */
  | "unknown";

/** Préfixe des clés que le service d'extraction crée pour une clé inconnue. */
const PREFIXE_TECHNIQUE = "extra_";

function champsDeclares(passId: string): readonly string[] {
  const passe = (EXTRACTION_PASSES as Record<string, { fields?: readonly string[] }>)[passId];
  return passe?.fields ?? [];
}

const ID_PASS = "identification";

/** Toutes les passes MÉTIER, c'est-à-dire tout sauf `identification`. */
const PASSES_METIER = Object.keys(EXTRACTION_PASSES).filter((p) => p !== ID_PASS);

/**
 * `field_key` → famille de garantie, construit depuis les contrats.
 *
 * Une famille n'est retenue que si le contrat déclare **au moins deux** champs sous
 * le même préfixe. Un préfixe unique ne forme pas une famille : il n'y aurait aucun
 * champ frère susceptible de porter une preuve négative, et l'appeler « famille »
 * suggérerait une structure qui n'existe pas.
 */
function construireFamilles(): Map<string, string> {
  const parPrefixe = new Map<string, string[]>();
  for (const passe of PASSES_METIER) {
    for (const cle of champsDeclares(passe)) {
      const i = cle.indexOf(".");
      if (i <= 0) continue; // champ plat → aucune famille
      const prefixe = cle.slice(0, i);
      const liste = parPrefixe.get(prefixe) ?? [];
      liste.push(cle);
      parPrefixe.set(prefixe, liste);
    }
  }
  const familles = new Map<string, string>();
  for (const [prefixe, cles] of parPrefixe) {
    if (cles.length < 2) continue;
    for (const cle of cles) familles.set(cle, prefixe);
  }
  return familles;
}

const FAMILLE_PAR_CHAMP: ReadonlyMap<string, string> = construireFamilles();

/** L'ensemble des familles, toutes issues des contrats. Aucune n'est inventée. */
const FAMILLES_DECLAREES: ReadonlySet<string> = new Set(FAMILLE_PAR_CHAMP.values());

/** Champs de garantie déclarés par les contrats des passes métier. */
const CHAMPS_GARANTIE: ReadonlySet<string> = new Set(PASSES_METIER.flatMap(champsDeclares));

/**
 * Champs de métadonnée du document.
 *
 * Union du contrat de la passe `identification` et du schéma de réponse
 * d'identification — ce dernier déclare les 10 clés que le modèle renvoie
 * réellement (`insurer_name`, `sections_present`, `uncertainty`…), là où le contrat
 * de passe n'en liste que les principales.
 */
const CHAMPS_METADONNEE: ReadonlySet<string> = new Set([
  ...champsDeclares(ID_PASS),
  ...Object.keys(IDENTIFICATION_RESPONSE_SCHEMA),
]);

/**
 * Famille de garantie d'un champ, ou `null` s'il n'en a aucune.
 *
 * `null` bloque tout rattachement de preuve négative pour ce champ — voir l'en-tête.
 */
export function guaranteeFamilyOf(fieldKey: string): string | null {
  const declare = FAMILLE_PAR_CHAMP.get(fieldKey);
  if (declare !== undefined) return declare;

  // Champ NON déclaré mais portant le préfixe d'une famille déclarée.
  //
  // Ce n'est pas la « proximité de nom improvisée » que la décision PO interdit :
  // l'ensemble des familles vient toujours des contrats, on n'en invente aucune. On
  // reconnaît seulement qu'un champ nommé `vie_base.reduction_percentage` appartient
  // à la famille `vie_base`, elle-même déclarée.
  //
  // Le cas est réel, pas théorique : le smoke 9.AA.1b a produit exactement ce champ,
  // que le contrat ne déclare pas (il déclare `vie_base.reduction_age`). Sans cette
  // règle, il perdait sa famille — et si `vie_base` était un jour non offert, ses
  // frères déclarés afficheraient `not_offered` pendant que lui afficherait
  // `not_detected`. Une incohérence visible dans une même famille, sur la même
  // ligne d'écran.
  //
  // Le risque inverse est borné : appartenir à une famille ne suffit jamais à
  // produire `not_offered`, il faut encore une mention négative explicite ET une
  // provenance. Élargir l'appartenance n'affaiblit donc aucune garantie.
  const i = fieldKey.indexOf(".");
  if (i <= 0) return null;
  const prefixe = fieldKey.slice(0, i);
  return FAMILLES_DECLAREES.has(prefixe) ? prefixe : null;
}

/** Les champs déclarés de la famille, dans l'ordre du contrat. */
export function fieldsOfGuaranteeFamily(family: string): readonly string[] {
  return [...FAMILLE_PAR_CHAMP.entries()].filter(([, f]) => f === family).map(([k]) => k);
}

/** Toutes les familles connues des contrats. */
export const GUARANTEE_FAMILIES: readonly string[] = [...new Set(FAMILLE_PAR_CHAMP.values())];

/**
 * Nature d'un champ. L'ordre des tests compte : un champ déclaré comme garantie le
 * reste même s'il porte par ailleurs un préfixe technique.
 */
export function fieldComparability(fieldKey: string): FieldComparability {
  if (CHAMPS_GARANTIE.has(fieldKey)) return "guarantee";
  // Un champ rattaché à une famille de garantie EST un champ de garantie, même si
  // le contrat ne le déclare pas nommément. Le classer `unknown` alors qu'il porte
  // `guarantee_family: "vie_base"` serait une incohérence dans la même charge — et
  // un conseiller comparerait quand même sa valeur.
  if (guaranteeFamilyOf(fieldKey) !== null) return "guarantee";
  if (CHAMPS_METADONNEE.has(fieldKey)) return "document_metadata";
  if (fieldKey.startsWith(PREFIXE_TECHNIQUE)) return "technical";
  return "unknown";
}

/**
 * Une ligne est-elle masquable de la vue comparative par défaut ?
 *
 * Décision PO : masquer les métadonnées du document, les débris techniques, et les
 * lignes vides chez **tous** les assureurs. Le masquage doit rester réversible,
 * visible et non destructif — c'est l'affaire de l'interface ; ici on ne fait que
 * dire ce qui est masquable, et pourquoi.
 *
 * `hasAnyValue` est décidé par l'appelant, qui seul voit toutes les cellules de la
 * ligne. Un champ de garantie vide partout est masquable ; un champ de garantie
 * renseigné ne l'est jamais, quelle que soit sa nature.
 */
export function rowHiddenByDefault(
  fieldKey: string,
  hasAnyValue: boolean,
  /**
   * Une cellule porte-t-elle une information même SANS valeur ? Typiquement un
   * `not_offered` avec sa preuve.
   *
   * Remarque P2 Codex, fondée : sans ce paramètre, une garantie qu'AUCUN assureur
   * n'offre était repliée comme « vide chez tous », alors que c'est exactement
   * l'information que ce chunk existe pour faire remonter. On aurait masqué par
   * défaut le seul cas où les trois assureurs s'accordent sur une absence — une
   * information de vente parfaitement exploitable.
   */
  hasMeaningfulStatus = false,
): { hidden: boolean; reason: RowHiddenReason | null } {
  const nature = fieldComparability(fieldKey);
  if (nature === "document_metadata") return { hidden: true, reason: "document_metadata" };
  if (nature === "technical") return { hidden: true, reason: "technical_field" };
  if (!hasAnyValue && !hasMeaningfulStatus) {
    return { hidden: true, reason: "empty_for_all_insurers" };
  }
  return { hidden: false, reason: null };
}

/** Motif de masquage — jamais `null` quand une ligne est masquée. */
export type RowHiddenReason = "document_metadata" | "technical_field" | "empty_for_all_insurers";
